/**
 * Every widget kind monday offers that we have **not** built (`specs/M10-D2-widgets.md` §5.5).
 *
 * Registered, not omitted, for the reason `specs/M4-V1-views.md` §5 gives for views: the gallery's
 * shape never changes when one lands, an imported widget keeps its real `ExternalWidget` kind
 * instead of degrading, and opening one renders a *coming soon* card rather than an error — or,
 * worse, a chart pretending to be a gantt. `settingsSchema` is a passthrough, so an imported
 * widget's settings survive a round trip untouched.
 */
import { z } from 'zod';
import { defineWidget } from '../types.js';
const passthrough = z.record(z.string(), z.unknown());
function placeholder(input) {
    return defineWidget({
        id: input.id,
        kind: input.kind,
        title: input.title,
        description: input.description,
        settingsSchema: passthrough,
        defaultSettings: {},
        dataShape: 'none',
        defaultLayout: input.defaultLayout ?? { x: 0, y: 0, w: 6, h: 6 },
        minLayout: { w: 3, h: 3 },
        requiresBoards: true,
        implemented: false,
        plannedIn: input.plannedIn,
        galleryOrder: input.galleryOrder,
        fromMondaySettings(raw) {
            if (typeof raw === 'string') {
                try {
                    const parsed = JSON.parse(raw);
                    return parsed !== null && typeof parsed === 'object' ? parsed : {};
                }
                catch {
                    return {};
                }
            }
            return raw !== null && typeof raw === 'object' ? raw : {};
        },
        toMondaySettings(settings) {
            return { ...settings };
        },
        dataRequest() {
            return null;
        },
    });
}
export const calendarWidget = placeholder({
    id: 'calendar', kind: 'CALENDAR', title: 'Calendar',
    description: 'Items with dates, on a month grid.', plannedIn: 'M4.V4', galleryOrder: 5,
});
export const ganttWidget = placeholder({
    id: 'gantt', kind: 'GANTT', title: 'Gantt',
    description: 'Timelines and dependencies across the connected boards.', plannedIn: 'M4.V3', galleryOrder: 6,
    defaultLayout: { x: 0, y: 0, w: 12, h: 8 },
});
export const tableWidget = placeholder({
    id: 'listview', kind: 'LISTVIEW', title: 'Table',
    description: 'The items themselves, as a list.', plannedIn: 'M7.D2b', galleryOrder: 7,
    defaultLayout: { x: 0, y: 0, w: 12, h: 8 },
});
/**
 * The generic app widget. The one battery on production is served this way `[vui]`, so an import
 * that meets an `APP_FEATURE` it cannot recognise lands here with its app url intact rather than
 * being dropped.
 */
export const appWidget = placeholder({
    id: 'app', kind: 'APP_FEATURE', title: 'App widget',
    description: 'A widget rendered by an app.', plannedIn: 'M10', galleryOrder: 8,
});
export const PLACEHOLDER_WIDGETS = [calendarWidget, ganttWidget, tableWidget, appWidget];
//# sourceMappingURL=index.js.map